import React, { useState } from "react";
import { MapPin, Mail, Clock } from "lucide-react";
import { sendContact } from "@/lib/api";
import { toast } from "sonner";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await sendContact(form);
      toast.success(res.message);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Message failed to send.");
    } finally {
      setSending(false);
    }
  };

  const field = (k) => ({
    value: form[k],
    onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })),
  });

  return (
    <div data-testid="contact-page" className="px-5 md:px-10 py-14 min-h-screen">
      <div className="border-b border-border pb-8 mb-12">
        <h1 className="font-serif text-6xl sm:text-7xl uppercase tracking-tighter leading-none">
          Contact
        </h1>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Reach the studio — Fort Worth, Texas
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-16">
        <div className="space-y-10">
          <div className="flex gap-4">
            <MapPin className="w-5 h-5 text-[#5B2A86] shrink-0 mt-1" />
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">
                Location
              </p>
              <p className="font-serif text-2xl">Fort Worth, Texas</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Mail className="w-5 h-5 text-[#5B2A86] shrink-0 mt-1" />
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">
                Email
              </p>
              <p className="font-serif text-2xl break-all">0011.Majnun.0011@gmail.com</p>
            </div>
          </div>
          <div className="flex gap-4">
            <Clock className="w-5 h-5 text-[#5B2A86] shrink-0 mt-1" />
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">
                Hours
              </p>
              <p className="font-serif text-2xl">Mon — Fri</p>
              <p className="font-mono text-sm text-muted-foreground">10:00 AM — 5:00 PM CST</p>
            </div>
          </div>
          <div className="overflow-hidden aspect-video border border-border">
            <iframe
              title="MAJNUN Fort Worth"
              src="https://www.google.com/maps?q=Fort+Worth,+Texas&output=embed"
              className="w-full h-full grayscale invert-[0.9] contrast-125"
              loading="lazy"
            />
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
          <div>
            <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
              Name
            </label>
            <input
              required
              data-testid="contact-name"
              {...field("name")}
              className="w-full mt-2 bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-foreground"
            />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
              Email
            </label>
            <input
              required
              type="email"
              data-testid="contact-email"
              {...field("email")}
              className="w-full mt-2 bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-foreground"
            />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
              Message
            </label>
            <textarea
              required
              rows={6}
              data-testid="contact-message"
              {...field("message")}
              className="w-full mt-2 bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-foreground resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit"
            className="w-full bg-foreground text-background py-4 text-xs font-mono uppercase tracking-[0.3em] hover:bg-[#5B2A86] hover:text-foreground transition-colors disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Contact;
